import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/api';
import { useVendor } from '../../context/VendorContext';
import OptimizedImage from '../utils/OptimizedImage';

const CategoryGrid = () => {
  const [showAll, setShowAll] = useState(true); // Changed to true to show all categories by default
  const [vendorCounts, setVendorCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const { refreshTrigger } = useVendor();

  // Fetch vendor counts from database
  useEffect(() => {
    const fetchVendorCounts = async () => {
      try {
        // First, test API connection
        console.log('🔄 Testing API connection...');
        console.log('🌐 API Base URL:', process.env.REACT_APP_API_URL || 'http://localhost:5000/api');
        
        // Test health endpoint first
        try {
          await apiService.healthCheck();
          console.log('✅ API Health check passed');
        } catch (healthError) {
          console.warn('⚠️ Health check failed, continuing with categories...');
        }

        // Fetch categories with vendor counts from API
        console.log('📊 Fetching categories...');
        const response = await apiService.getCategories();
        
        if (response.success) {
          const counts = {};
          
          // Map category slugs to vendor counts
          const categorySlugMap = {
            'catering': 'catering-services',
            'decorators': 'decorators',
            'bands': 'bands-banjo',
            'photographers': 'photographers-content-creators',
            'makeup': 'makeup-artists',
            'venues': 'venues',
            'dj': 'dj-music',
            'invitations': 'invitation-cards',
            'fashion': 'function-wear',
            'decorations': 'stage-entrance-decorations',
            'cakes': 'cakes-gift-shops',
            'anchors': 'anchors-hosts',
            'entertainment': 'entertainment-extras',
            'sweets': 'sweets-snacks'
          };

          response.categories.forEach(category => {
            const frontendSlug = categorySlugMap[category.slug];
            if (frontendSlug) {
              counts[frontendSlug] = category.vendorCount || 0;
            }
          });
          
          setVendorCounts(counts);
        }
      } catch (error) {
        console.error('Error fetching vendor counts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVendorCounts();
  }, [refreshTrigger]);

  // Function to get vendor count for a category
  const getVendorCount = (slug) => {
    return vendorCounts[slug] || 0;
  };

  // All 14 vendor categories with proper slugs in exact order provided
  const allCategories = [
    {
      id: '1',
      name: 'Catering Services',
      slug: 'catering-services',
      image: 'https://images.unsplash.com/photo-1555244162-803834f70033?w=400&h=300&fit=crop'
    },
    {
      id: '2',
      name: 'Decorators',
      slug: 'decorators',
      image: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=400&h=300&fit=crop'
    },
    {
      id: '3',
      name: 'Bands & Banjo',
      slug: 'bands-banjo',
      image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop'
    },
    {
      id: '4',
      name: 'Photographers & Content Creators',
      slug: 'photographers-content-creators',
      image: 'https://images.unsplash.com/photo-1606800052052-a08af7148866?w=400&h=300&fit=crop'
    },
    {
      id: '5',
      name: 'Makeup Artists',
      slug: 'makeup-artists',
      image: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=400&h=300&fit=crop'
    },
    {
      id: '6',
      name: 'Venues',
      slug: 'venues',
      image: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=400&h=300&fit=crop'
    },
    {
      id: '7',
      name: 'DJ & Music',
      slug: 'dj-music',
      image: 'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=400&h=300&fit=crop'
    },
    {
      id: '8',
      name: 'Invitation Cards',
      slug: 'invitation-cards',
      image: 'https://images.unsplash.com/photo-1511988617509-a57c8a288659?w=400&h=300&fit=crop'
    },
    {
      id: '9',
      name: 'Function Wear',
      slug: 'function-wear',
      image: 'https://res.cloudinary.com/dgban38oz/image/upload/v1759926003/Gemini_Generated_Image_p94rrup94rrup94r_cn4pus.png'
    },
    {
      id: '10',
      name: 'Stage & Entrance Decorations',
      slug: 'stage-entrance-decorations',
      image: 'https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=400&h=300&fit=crop'
    },
    {
      id: '11',
      name: 'Cakes & Gift Shops',
      slug: 'cakes-gift-shops',
      image: 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=400&h=300&fit=crop'
    },
    {
      id: '12',
      name: 'Anchors & Hosts',
      slug: 'anchors-hosts',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop'
    },
    {
      id: '13',
      name: 'Entertainment & Extras',
      slug: 'entertainment-extras',
      image: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=400&h=300&fit=crop'
    },
    {
      id: '14',
      name: 'Sweets & Snacks',
      slug: 'sweets-snacks',
      image: 'https://res.cloudinary.com/dgban38oz/image/upload/v1761673801/Gemini_Generated_Image_xnmsluxnmsluxnms_leh6lu.png'
    }
  ];

  // Top 8 categories to display initially
  const topCategories = allCategories.slice(0, 8);
  const categoriesToShow = showAll ? allCategories : topCategories;

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Find Your Perfect Ceremony Vendors
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover trusted professionals for every aspect of your special day
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            <div className={`grid gap-6 ${showAll ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'}`}>
              {categoriesToShow.map((category) => {
                return (
                  <Link
                    key={category.id}
                    to={`/category/${category.slug}`}
                    className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  >
                    <div className="aspect-w-4 aspect-h-3">
                      <OptimizedImage
                        src={category.image}
                        alt={category.name}
                        width={400}
                        height={300}
                        className="w-full h-48 group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                      <h3 className="text-lg font-semibold mb-1 text-shadow">{category.name}</h3>
                    </div>
                    
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-blue-600 bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                      <div className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            <div className="text-center mt-12">
              <button
                onClick={() => setShowAll(!showAll)}
                className="inline-flex items-center px-8 py-4 bg-blue-600 text-white border border-transparent text-lg font-semibold rounded-xl hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                {showAll ? 'Show Top 8 Categories' : 'View All 14 Categories'}
                <svg className={`ml-3 w-5 h-5 transition-transform duration-300 ${showAll ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default CategoryGrid;